import { PageLayout } from "@/components";
import { useTranslation } from "@/hooks/useTranslation";
import { Route } from "@/routes/_market/orders/index";
import { INTENT_PERMISSIONS, userIntentAtom } from "@/store/login";
import { ordersTabAtom } from "@/store/order";
import {
	Button,
	Icon,
	TabItem,
	Tabs,
	TabsList,
	Typography,
} from "@st-marketplace/ui";
import { useNavigate } from "@tanstack/react-router";
import { useAtom, useAtomValue } from "jotai";
import { type FC, useEffect, useMemo } from "react";
import OrdersAddDialog from "./OrdersAddDialog/Index";
import OrdersContent from "./OrdersContent";

const Orders: FC<{ isAdmin: boolean }> = ({ isAdmin }) => {
	const [tabValue, setTabValue] = useAtom(ordersTabAtom);
	const userIntent = useAtomValue(userIntentAtom);
	const { t } = useTranslation();

	const navigate = useNavigate({ from: "/orders" });
	const search = Route.useSearch();

	// Determine available tabs and default tab based on market intent
	const intentPermissions = userIntent
		? INTENT_PERMISSIONS[userIntent].orders
		: null;
	const showBuyerTab = intentPermissions?.showBuyerTab ?? true; // default to true for backward compatibility
	const showSellerTab = intentPermissions?.showSellerTab ?? true; // default to true for backward compatibility
	const defaultTab = intentPermissions?.defaultTab ?? 0;

	const allowedTabs = useMemo(() => {
		const tabs: number[] = [];
		if (showBuyerTab) tabs.push(0);
		if (showSellerTab) tabs.push(1);
		return tabs;
	}, [showBuyerTab, showSellerTab]);

	useEffect(() => {
		if (!allowedTabs.includes(tabValue)) {
			setTabValue(defaultTab);
		}
	}, [allowedTabs, tabValue, defaultTab, setTabValue]);

	const handleTabChange = (value: string) => {
		setTabValue(value === "seller" ? 1 : 0);
	};

	const handleOpenDialog = () => {
		navigate({
			search: (prev) => ({ ...prev, new: true }),
		});
	};

	const handleCloseDialog = () => {
		navigate({
			search: (prev) => ({ ...prev, new: false }),
		});
	};

	const schemaType = isAdmin
		? "admin"
		: tabValue === 0 && showBuyerTab
			? "buyer"
			: tabValue === 1 && showSellerTab
				? "seller"
				: allowedTabs[0] === 0
					? "buyer"
					: "seller";

	return (
		<PageLayout>
			<div className="flex flex-col-reverse md:flex-row md:items-center md:justify-between">
				{isAdmin ? (
					<Typography variant="title-md" as="h1">
						{t("admin.orders.title")}
					</Typography>
				) : (
					<Tabs
						value={tabValue === 1 ? "seller" : "buyer"}
						onValueChange={handleTabChange}
					>
						<TabsList>
							{showBuyerTab && (
								<TabItem value="buyer" Icon={<Icon name="file-list-3-line" />}>
									{t("order.tabOutgoing")}
								</TabItem>
							)}
							{showSellerTab && (
								<TabItem value="seller" Icon={<Icon name="download-2-line" />}>
									{t("order.tabIncoming")}
								</TabItem>
							)}
						</TabsList>
					</Tabs>
				)}
				{isAdmin ? null : (
					<div className="border-b border-secondary-border pb-3">
						<Button
							type="button"
							color="primary"
							className="w-full md:w-auto"
							startIcon={<Icon name="add-line" />}
							onClick={handleOpenDialog}
						>
							{t("order.addButton")}
						</Button>
					</div>
				)}
			</div>

			<OrdersContent schemaType={schemaType} />

			{isAdmin ? null : (
				<OrdersAddDialog open={search.new} onClose={handleCloseDialog} />
			)}
		</PageLayout>
	);
};

export default Orders;
